import { useStytchMember } from "@stytch/react/b2b";
import moment from "moment-timezone";
import { useListLocations, useListLocationRuns, useListRunHours, useListRunHoursPaired } from "@/lib/api/destinationHours";
import type { DestinationHoursLocation, DestinationHoursLocationRun, DestinationHoursRunHours } from "@/lib/api/destinationHours";
import { cn, CommonDestinationTimezone, GenerateLoadingOrError } from "@/lib/utils";
import { ArrowLeft, PlusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useEffect, useMemo, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader } from "@/components/ui/loader";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { downloadFromS3 } from "@/lib/file";
import { Separator } from "@/components/ui/separator";
import { DataTable, DataTableProps, } from "@/components/ui/virtualizedTable";

export function DestinationHoursDashboard() {
    const { member } = useStytchMember();
    const [tab, setTab] = useState<"locations" | "location_runs" | "location_run">("locations");
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        if (!searchParams.get("locationId") && tab !== "locations") {
            setTab("locations");
        } else if (searchParams.get("locationId") && !searchParams.get("runId") && tab !== "location_runs") {
            setTab("location_runs");
        } else if (searchParams.get("locationId") && !["location_run", "location_runs"].includes(tab)) {
            setTab("location_runs");
        } else if (searchParams.get("locationId") && searchParams.get("runId") && tab !== "location_run") {
            setTab("location_run");
        }
    }, [searchParams, tab]);

    const onTabChange = (value: string) => {
        setTab(value as any);
        if (value === "locations") {
            setSearchParams({});
        } else if (value === "location_runs") {
            setSearchParams({ locationId: searchParams.get("locationId")! });
        } else if (value === "location_run") {
        }
    };

    return (
        <section className="flex min-h-full flex-1 flex-col justify-start px-6 lg:px-8">
            <div className="flex items-center justify-between mb-6">
                <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
                    Destination Hours
                </h1>
            </div>

            <Tabs value={tab} onValueChange={onTabChange} className="space-y-6">
                <TabsList>
                    <TabsTrigger value="locations">Locations</TabsTrigger>
                    <TabsTrigger value="location_runs" disabled>Location Runs</TabsTrigger>
                    <TabsTrigger value="location_run" disabled>Location Run</TabsTrigger>
                </TabsList>

                <TabsContent value="locations" className="space-y-4">
                    <DestinationHoursLocationsView />
                </TabsContent>

                <TabsContent value="location_runs" className="space-y-4">
                    <DestinationHoursLocationRunsView />
                </TabsContent>

                <TabsContent value="location_run" className="space-y-4">
                    <DestinationHoursLocationRunView />
                </TabsContent>
            </Tabs>
        </section>
    )
}

function DestinationHoursLocationsView() {
    const locationsRes = useListLocations();
    const errorOrLoading = GenerateLoadingOrError([locationsRes]);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const originalLocationsData = locationsRes.data?.data || [];

    const locationsData = useMemo(() => {
        const data = originalLocationsData.map((location: any) => {
            return {
                ...location,
            }
        })

        data.sort((a: any, b: any) => {
            if (a.is_visible !== b.is_visible) {
                return a.is_visible ? -1 : 1;
            }

            if (a.is_enabled !== b.is_enabled) {
                return a.is_enabled ? -1 : 1;
            }

            if (a.name !== b.name) {
                return a.name.localeCompare(b.name);
            }

            return 0;
        })

        return data;
    }, [originalLocationsData]);

    if (errorOrLoading) return errorOrLoading;

    const jumpToRun = (locationId: string, runId: string) => () => {
        setSearchParams({ locationId, runId });
    };



    const columns: any = [
        {
            header: "Name",
            accessorKey: "name",
            onCellClick: true,
            cell: ({ row }: any) => {
                return <div>{row.original.alias || row.original.name}</div>
            },enableColumnFilter: false,
        },
        {
            header: "most_recent",
            accessorKey: "Most Recent",
            isCellClick: false,
            cell: ({ row }: any) => {
                return <Button disabled={!row.original.most_recent_run} size="sm" variant="ghost" onClick={jumpToRun(row.original.id, row.original.most_recent_run)}>{row.original.most_recent_run ? "Most Recent": "" }</Button>
            },enableColumnFilter: false,
        },
        {
            header: "Link",
            accessorKey: "link",
            isCellClick: false,
            cell: ({ row }: any) => {
                return <a href={row.original.link} target="_blank" rel="noopener noreferrer">{row.original.link}</a>
            },enableColumnFilter: false,
        },
        {
            header: "Last Fetched",
            accessorKey: "last_fetched",
            onCellClick: true,
            cell: ({ row }: any) => {
                return <div>{row.original.last_fetched ? moment(row.original.last_fetched).format("MM/DD/YYYY HH:mm") : "Never"}</div>
            },enableColumnFilter: false,
        },
        {
            header: "Last Extracted",
            accessorKey: "last_parsed",
            onCellClick: true,
            cell: ({ row }: any) => {
                return <div>{row.original.last_parsed ? moment(row.original.last_parsed).format("MM/DD/YYYY HH:mm") : "Never"}</div>
            },enableColumnFilter: false,
        },
        {
            header: "Last Updated",
            accessorKey: "last_ingest",
            onCellClick: true,
            cell: ({ row }: any) => {
                return <div>{row.original.last_ingest ? moment(row.original.last_ingest).format("MM/DD/YYYY HH:mm") : "Never"}</div>
            },enableColumnFilter: false,
        },
        {
            header: "Will Run",
            accessorKey: "is_enabled",
            onCellClick: true,
            cell: ({ row }: any) => {
                return <div>{row.original.is_enabled ? "Yes" : "No"}</div>
            },enableColumnFilter: false,
        }
    ]

    const onRowClick = (row: DestinationHoursLocation) => {
        setSearchParams({ locationId: row.id });
    }

    return (
        <div className="flex flex-col gap-4">
            <h2 className="text-lg font-semibold">Locations</h2>
            <DataTable columns={columns} data={locationsData.filter((item: any) => item.is_visible)} height="500px" onCellClick={onRowClick} />
        </div>
    )
}

function DestinationHoursLocationRunsView() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const locationRunsRes = useListLocationRuns({ locationId: searchParams.get("locationId")! });
    const errorOrLoading = GenerateLoadingOrError([locationRunsRes]);

    if (errorOrLoading) return errorOrLoading;


    const locationRunsData = locationRunsRes.data?.data || [];

    const columns: any = [
        {
            header: "Location Name",
            accessorKey: "name",
            onCellClick: true,
            cell: ({ row }: any) => {
                return <div>{row.original.location_name}</div>
            },enableColumnFilter: false,
        },
        {
            header: "Run At",
            accessorKey: "run_at",
            isCellClick: false,
            cell: ({ row }: any) => {
                return <div>{moment(row.original.run_at).format("MM/DD/YYYY HH:mm")}</div>
            },enableColumnFilter: false,
        },
    ]

    const onRowClick = (row: DestinationHoursLocationRun) => {
        setSearchParams({ locationId: row.location_id, runId: row.run_id });
    }

    return (
        <div className="flex flex-col gap-4">
            <h2 className="text-lg font-semibold">Location Runs</h2>
            <DataTable columns={columns} data={locationRunsData} height="500px" onCellClick={onRowClick} />
        </div>
    )
}

function DestinationHoursLocationRunView() {
    const navigate = useNavigate();
    const { member } = useStytchMember();
    const [searchParams, setSearchParams] = useSearchParams();
    const runHoursRes = useListRunHoursPaired({ locationId: searchParams.get("locationId")!, runId: searchParams.get("runId")! });
    const [tab, setTab] = useState<"fetched" | "cleaned">("fetched");
    const runHoursData = runHoursRes.data?.data?.requested || [];
    const previousHoursData = runHoursRes.data?.data?.previous || [];

    const isAdmin = member?.trusted_metadata["role"] === "admin" || false;

    const { fetchedData, cleanedData, previousFetchedData } = useMemo(() => {
        const fetchedData = runHoursData.flatMap((run: any) => {
            return run.location_hours.flatMap((locationGroup: any) => {
                return locationGroup.operating_hours.flatMap((operatingHour: any) => {
                    return operatingHour.original_hours.flatMap((hour: any) => {
                        return {
                            link: run.link,
                            location_name: locationGroup.original_location_name,
                            group_name: operatingHour.original_group_name,

                            date: hour.date,
                            range: hour.range,
                            open: hour.open,
                            close: hour.close,
                            note: hour.note,
                        }
                    });
                });
            });
        });

        const previousFetchedData = previousHoursData.flatMap((run: any) => {
            return run.location_hours.flatMap((locationGroup: any) => {
                return locationGroup.operating_hours.flatMap((operatingHour: any) => {
                    return operatingHour.original_hours.flatMap((hour: any) => {
                        return {
                            link: run.link,
                            location_name: locationGroup.original_location_name,
                            group_name: operatingHour.original_group_name,

                            date: hour.date,
                            range: hour.range,
                            open: hour.open,
                            close: hour.close,
                            note: hour.note,
                        }
                    });
                });
            });
        });

        const cleanedData = runHoursData.flatMap((run: any) => {
            return run.location_hours.flatMap((locationGroup: any) => {
                return locationGroup.operating_hours.flatMap((operatingHour: any) => {
                    return operatingHour.hours_info.flatMap((hour: any) => {
                        return {
                            location_name: locationGroup.location_name,
                            group_name: operatingHour.group_name,

                            date: hour.date,
                            range: hour.range,
                            open: hour.open,
                            close: hour.close,
                            note: hour.note,
                        }
                    });
                });
            });
        });

        fetchedData.sort((a: any, b: any) => {
            if (a.location_name !== b.location_name) {
                return a.location_name.localeCompare(b.location_name);
            }

            if (a.group_name !== b.group_name) {
                return a.group_name.localeCompare(b.group_name);
            }

            if (a.date && !b.date && b.range) {
                return 1;
            }

            if (!a.date && b.date) {
                return -1;
            }

            if (a.date && b.date && a.date !== b.date) {
                return new Date(a.date).getTime() - new Date(b.date).getTime();
            }

            if (a.range && b.range && a.range !== b.range) {
                return a.range.localeCompare(b.range);
            }

            return 0;
        });

        cleanedData.sort((a: any, b: any) => {
            if (a.location_name !== b.location_name) {
                return a.location_name.localeCompare(b.location_name);
            }

            if (a.group_name !== b.group_name) {
                return a.group_name.localeCompare(b.group_name);
            }

            if (a.date && !b.date && b.range) {
                return 1;
            }

            if (!a.date && b.date) {
                return -1;
            }

            if (a.date && b.date && a.date !== b.date) {
                return new Date(a.date).getTime() - new Date(b.date).getTime();
            }

            if (a.range && b.range && a.range !== b.range) {
                return a.range.localeCompare(b.range);
            }

            return 0;
        });

        previousFetchedData.sort((a: any, b: any) => {
            if (a.location_name !== b.location_name) {
                return a.location_name.localeCompare(b.location_name);
            }

            if (a.group_name !== b.group_name) {
                return a.group_name.localeCompare(b.group_name);
            }

            if (a.date && !b.date && b.range) {
                return 1;
            }

            if (!a.date && b.date) {
                return -1;
            }

            if (a.date && b.date && a.date !== b.date) {
                return new Date(a.date).getTime() - new Date(b.date).getTime();
            }

            if (a.range && b.range && a.range !== b.range) {
                return a.range.localeCompare(b.range);
            }

            return 0;
        });


        return { fetchedData, cleanedData, previousFetchedData };
    }, [runHoursData, previousHoursData]);

    const errorOrLoading = GenerateLoadingOrError([runHoursRes]);

    if (errorOrLoading) return errorOrLoading;

    const columns: any = [
        {
            header: "Location Name",
            accessorKey: "name",
            cell: ({ row }: any) => {
                return <div>{row.original.location_name}</div>
            },enableColumnFilter: false,
        },
        {
            header: "Group Name",
            accessorKey: "group_name",
            cell: ({ row }: any) => {
                return <div>{row.original.group_name}</div>
            },enableColumnFilter: false,
        },
        {
            header: "Date",
            accessorKey: "date",
            cell: ({ row }: any) => {
                return <div>{row.original.date || row.original.range || ""}</div>
            },enableColumnFilter: false,
        },
        {
            header: "Open",
            accessorKey: "open",
            cell: ({ row }: any) => {
                return <div>{row.original.open || ""}</div>
            },enableColumnFilter: false,
        },
        {
            header: "Close",
            accessorKey: "close",
            cell: ({ row }: any) => {
                return <div>{row.original.close || ""}</div>
            },enableColumnFilter: false,
        },
        {
            header: "Note",
            accessorKey: "note",
            cell: ({ row }: any) => {
                return <div>{row.original.note || ""}</div>
            },enableColumnFilter: false,
        },
    ]

    if (!cleanedData.length) {
        return (
            <div className="flex flex-col gap-4">
                <h2 className="text-lg font-semibold">Location Run Hours</h2>
                <div className="flex flex-col gap-4">
                    <span>No data</span>
                </div>
            </div>
        )
    }

    const goToLocationRunList = () => {
        setSearchParams({ locationId: searchParams.get("locationId")! });
    }

    return (
        <div className="flex flex-col gap-4">
            <h2 className="text-lg font-semibold">
                <Button variant={"ghost"} size="sm" onClick={goToLocationRunList}>
                    <ArrowLeft className="w-4 h-4" />
                </Button>
                Location Run Hours
            </h2>
            <div className="flex flex-col gap-1">
                <span><span className="font-bold">
                    {!!fetchedData?.[0]?.link ? (<a href={`${fetchedData[0].link}`} className="underline decoration-solid decoration-sky-500" target="_blank">{cleanedData[0].location_name}</a>): (cleanedData[0].location_name)}
                </span> at <span className="text-xs ">{moment.tz(runHoursData[0].run_at, CommonDestinationTimezone).format("MM/DD/YYYY hh:mm a")}</span></span>
                {isAdmin && tab === "cleaned" && (
                    <p className="text-sm text-muted-foreground">
                        The "Extracted Version" is meant to give you insight into how the system thinks about the data (for debugging purposes), please view the "Website Version" for more readable data.
                        <br />
                        This is a version of the data that was extracted from the website and transformed to more support notifications.
                        <br />
                        Switch to the <span className="font-bold cursor-pointer" onClick={() => setTab("fetched")}>Website Version</span> to see the original data as extracted by the AI.
                        <br />
                        It may show old note info, as it only uses the note for diffing when the open/close/date is not present.
                        <br />
                        It also attempts to strip away extra date info, such as "April 17th" from "Thursday, April 17th" so it can compare against previous data.
                    </p>
                )}
                {tab === "fetched" && (
                    <p className="text-sm text-muted-foreground">
                        This is the original data as extracted by the AI.
                        <br />
                        Switch to the <span className="font-bold cursor-pointer" onClick={() => setTab("cleaned")}>Extracted Version</span> to see the data as transformed to more support notifications.
                    </p>
                )}
            </div>


            <Tabs value={tab} onValueChange={(value) => setTab(value as any)} className="space-y-6">
                <TabsList>
                    <TabsTrigger value="fetched">Website Version</TabsTrigger>
                    {isAdmin && (<TabsTrigger value="cleaned">Extracted Version</TabsTrigger>)}
                </TabsList>

                <TabsContent value="fetched" className="space-y-4">
                    <DataTable columns={columns} data={fetchedData} height="500px" />
                    {previousHoursData.length > 0 && (
                    <div className="flex flex-col gap-2">
                        <h2 className="text-bold text-lg">Previous Run</h2>
                        <DataTable columns={columns} data={previousFetchedData} height="500px" />
                    </div>
                    )}
                </TabsContent>

                {isAdmin && (<TabsContent value="cleaned" className="space-y-4">
                    <DataTable columns={columns} data={cleanedData} height="500px" />
                </TabsContent>)}
            </Tabs>
        </div>
    )
}
